import { Spacing } from '@persona-id/design-tokens';
import { PDSIconComponent } from '@persona-id/design-tokens/icons/types';
import { ComponentType, SVGAttributes } from 'react';
import { connect, ConnectedProps, useSelector } from 'react-redux';
import styled from 'styled-components';

import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { OrganizationStatus } from 'dashboard/models/organization';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Display, Paragraph } from 'design-system/components/Text';

interface ProductUse {
  icon: PDSIconComponent;
  usesTitle: string;
  usesDescription: string;
}

export interface DisabledProductPageProps {
  id: string;
  title: string;
  headerTitle: string;
  description: string;
  uses: ProductUse[];
  image: ComponentType<SVGAttributes<SVGElement>>;
}

const CONTACT_SALES_PAGE =
  'https://withpersona.com/contact?utm_source=product&utm_medium=referral&utm_audience=a&utm_campaign=acq_gen_ds_general-in-dashboard-plg-pls';

const DisabledProductPage = ({
  id,
  title,
  headerTitle,
  description,
  uses,
  image,
  organization,
}: DisabledProductPageProps & PropsFromRedux) => {
  const pendingOrganizationPlanMigration = useSelector(
    (state: RootState) => state.organization.pendingOrganizationPlanMigration,
  );
  const Image = image;
  const hasNotFinishedProductionAccessProcess =
    organization?.attributes.status === OrganizationStatus.New && !pendingOrganizationPlanMigration;
  const nextStepUrl = hasNotFinishedProductionAccessProcess
    ? 'https://app.withpersona.com/dashboard/billing'
    : `${CONTACT_SALES_PAGE}`;

  return (
    <Container data-test='disabled-product-page'>
      <Header title={headerTitle}></Header>
      <Section>
        <CardContainer>
          <Intro>
            <Display size='sm'>{title}</Display>
            <Spacer height={3} />
            <Paragraph color='primary' size='lg'>
              {description}
            </Paragraph>
            <Spacer height={3} />
            {uses.map((use) => {
              const Icon = use.icon;
              return (
                <ProductUsesWrapper key={use.usesTitle}>
                  <UsesIcon>
                    <Icon />
                  </UsesIcon>
                  <Paragraph color='primary'>
                    <UsesTitle>{use.usesTitle}</UsesTitle> {use.usesDescription}
                  </Paragraph>
                </ProductUsesWrapper>
              );
            })}
            <Spacer height={5} />
            <ActionButtons>
              <Button
                id={`disabled-product-page__start-tour__${id}`}
                data-marketing-id={`disabled-product-page__start-tour__${id}`}
                variant='basic'
                aria-label='Start Tour'
              >
                Start Tour
              </Button>
              <a href={nextStepUrl} target='_blank' rel='noreferrer'>
                <Button id={`disabled-product-page__next-step__${id}`} aria-label='Talk to Sales'>
                  {hasNotFinishedProductionAccessProcess ? 'Continue activating' : 'Talk to Sales'}
                </Button>
              </a>
            </ActionButtons>
          </Intro>
          <ProductImage>
            <Image />
          </ProductImage>
        </CardContainer>
      </Section>
    </Container>
  );
};

const CardContainer = styled(Card)`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  column-gap: ${Spacing[7]};
  padding: ${Spacing[9]} ${Spacing[9]} 0 ${Spacing[9]};
  > * {
    margin-bottom: ${Spacing[9]};
  }
`;

const ProductImage = styled('div')`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 350px;
  width: auto;
  height: auto;
`;

const Intro = styled('div')`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ActionButtons = styled('div')`
  display: grid;
  height: fit-content;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  grid-gap: ${Spacing[4]};
`;

const UsesTitle = styled('span')`
  font-weight: bold;
`;

const UsesIcon = styled('div')`
  margin-right: ${Spacing[4]};
  margin-top: ${Spacing[1]};
  padding: ${Spacing[2]};
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #a9ecd3;
`;

const ProductUsesWrapper = styled('div')`
  display: flex;
  margin: ${Spacing[2]} 0px ${Spacing[2]} 0px;
  padding: ${Spacing[4]}, 0px;
  width: 100%;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
`;

const mapStateToProps = (state: RootState) => ({
  organization: state.organization.organization,
});

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DisabledProductPage);
